import {
  ListType,
  ListsEditor,
  onKeyDown,
  withLists as withListsPluginCreator,
  withListsReact,
} from '@prezly/slate-lists'
import { SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import {
  splitOffCurrentLine,
  textBeforeCaret,
} from '@src/components/sco/RichMarkdownInput/plugins/lineBreaks'
import { isHotkey } from 'is-hotkey'
import { Editor, Element, Node, NodeEntry, Range } from 'slate'

const BULLET_TRIGGERS = ['-', '*', '•', '--']

export function withLists(editor, options = {}) {
  const editorWithLists = withListsReact(
    withListsPluginCreator({
      isConvertibleToListTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.PARAGRAPH)
      },
      isDefaultTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.PARAGRAPH)
      },
      isListNode(node: Node, type: ListType) {
        if (type) {
          return Element.isElementType(node, type)
        }
        return (
          Element.isElementType(node, EditorElementTypes.OL_LIST) ||
          Element.isElementType(node, EditorElementTypes.UL_LIST)
        )
      },
      isListItemNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.LIST_ITEM)
      },
      isListItemTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.LIST_ITEM_TEXT)
      },
      createDefaultTextNode(props = {}) {
        return { type: EditorElementTypes.PARAGRAPH, children: [{ text: '' }], ...props }
      },
      createListNode(type: ListType = ListType.ORDERED, props = {}) {
        const nodeType = type === ListType.ORDERED ? EditorElementTypes.OL_LIST : EditorElementTypes.UL_LIST
        return { type: nodeType, children: [{ text: '' }], ...props }
      },
      createListItemNode(props = {}) {
        return { type: EditorElementTypes.LIST_ITEM, children: [{ text: '' }], ...props }
      },
      createListItemTextNode(props = {}) {
        return {
          type: EditorElementTypes.LIST_ITEM_TEXT,
          children: [{ type: EditorElementTypes.PARAGRAPH, children: [{ text: '' }] }],
          ...props,
        }
      },
    })(editor)
  )

  const onKeyDownOriginal = editor.onKeyDown
  editorWithLists.onKeyDown = (editor, event) => {
    onKeyDown(editorWithLists, event)

    if (isHotkey('backspace', event.nativeEvent)) {
      const { selection } = editorWithLists

      if (selection && Range.isCollapsed(selection)) {
        const [emptyNode]: Generator<NodeEntry<Node & { children: { text?: string }[] }>> = Editor.nodes(
          editor,
          {
            match: node =>
              Element.isElementType(node, EditorElementTypes.LIST_ITEM_TEXT) &&
              (node as SlateNode).children[0].text == '',
            at: selection,
          }
        )

        if (emptyNode) {
        }
      }
    }

    if (isHotkey('space', event.nativeEvent)) {
      const { selection } = editorWithLists
      const paragraph =
        selection &&
        Range.isCollapsed(selection) &&
        Editor.above(editor, { match: node => Element.isElementType(node, EditorElementTypes.PARAGRAPH) })

      if (paragraph) {
        // The shortcut is the current line, so it also works after a Shift+Enter line break
        const before = textBeforeCaret(editor, paragraph[1])
        const line = before.slice(before.lastIndexOf('\n') + 1)
        const listType = BULLET_TRIGGERS.includes(line)
          ? ListType.UNORDERED
          : /^\d+[.]$/.test(line)
            ? ListType.ORDERED
            : null

        if (listType) {
          event.preventDefault()
          splitOffCurrentLine(editor, line, before)
          ListsEditor.wrapInList(editor, listType)
        }
      }
    }

    if (onKeyDownOriginal) {
      onKeyDownOriginal(editor, event)
    }
  }

  return editorWithLists
}
